import React from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Trash2,
  GripVertical,
  Calculator,
  Paperclip,
  ArrowLeftRight,
  FlaskConical,
  ChevronDown,
  ChevronUp,
  Package,
  ShoppingCart,
  Truck,
  Factory,
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../lib/utils';
import EditableCell from './EditableCell';

const ProductTableRow = ({
  product,
  index,
  currentColumns,
  readOnly,
  editingCell,
  isEditing,
  onCellEdit,
  onCellValueChange,
  onCellKeyPress,
  onCellSave,
  onPriceLookup,
  onReplace,
  onDelete,
  onProductIdClick,
  calculateColumnValue,
  isCalculatedColumn,
  taxRate,
  hasAttachments = false,
  onAttachmentClick,
  isSample = false,
  canViewPrices = true,
  executionProgress = null,
  isExpanded = false,
  onToggleExpand,
  enableExpandableProgress = false,
  onInventoryClick,
}) => {
  const id = product.line_item_id || product.id || product._id || `item-${index}`;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: readOnly });

  const style = readOnly ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handlePriceLookupClick = () => {
    if (onPriceLookup) {
      onPriceLookup(index, product);
    }
  };

  const handleReplaceClick = () => {
    if (onReplace) {
      onReplace(index, product);
    }
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(product.line_item_id || product._id || product.id, index);
    }
  };

  const productLineId = product.line_item_id || String(product._id || '');
  const required = product.quantity ?? 0;

  let inventoryData = null;
  let productionData = null;
  let procurementData = {
    allocated_qty: 0,
    received_qty: 0,
    pending_qty: 0,
    required_qty: 0,
    pending_po_creation: 0,
  };
  let warehouseData = {
    inbound: { received_qty: 0, pending_qty: 0 },
    outbound: { shipped_qty: 0, pending_qty: 0 },
  };
  let breakdown = null;

  if (executionProgress?.per_product?.[productLineId]) {
    const progressData = executionProgress.per_product[productLineId];

    inventoryData = {
      available_quantity: progressData.stock_at_creation,
      reserved_quantity: progressData.frozen_qty || 0,
      remaining_quantity: progressData.stock_at_creation - (progressData.frozen_qty || 0),
    };

    productionData = progressData.production;

    procurementData = {
      allocated_qty: progressData.allocated_to_po,
      received_qty: progressData.received_qty,
      pending_qty: progressData.pending_receipt,
      required_qty: progressData.required_qty,
      pending_po_creation: progressData.pending_po_creation,
    };

    warehouseData = {
      inbound: {
        received_qty: progressData.received_qty,
        pending_qty: progressData.pending_receipt,
      },
      outbound: {
        shipped_qty: progressData.shipped_qty,
        pending_qty: progressData.pending_shipment,
      },
    };

    breakdown = {
      from_stock: progressData.from_stock,
      from_procurement: progressData.needs_procurement,
      stockCoverage: progressData.from_stock,
      stockFulfilledPct: progressData.required_qty > 0
        ? (progressData.from_stock / progressData.required_qty) * 100
        : 0,
      procurementAllocated: progressData.allocated_to_po,
      procurementFulfilledPct: progressData.needs_procurement > 0
        ? (progressData.allocated_to_po / progressData.needs_procurement) * 100
        : 0,
    };
  }

  let overallProgress = 0;
  if (executionProgress?.per_product?.[productLineId]) {
    overallProgress = executionProgress.per_product[productLineId].progress?.overall_progress_pct || 0;
  }

  return (
    <>
      <tr
        ref={setNodeRef}
        style={style}
        key={product.id || product._id || index}
        className={cn(
          'hover:bg-muted/50',
          isDragging ? 'cursor-grabbing bg-muted' : 'cursor-default'
        )}
      >
        <td className="p-3 border-b">
          <div className="flex items-center gap-1">
            {!readOnly && (
              <GripVertical
                {...attributes}
                {...listeners}
                className={cn(
                  'mr-2 h-5 w-5',
                  isDragging ? 'text-primary cursor-grabbing' : 'text-muted-foreground cursor-grab',
                  'hover:text-primary'
                )}
              />
            )}
            {enableExpandableProgress && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 p-0 mr-1"
                onClick={onToggleExpand}
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            )}
            <span>{index + 1}</span>
            {isSample && (
              <FlaskConical className="h-4 w-4 text-purple-500" title="样品" />
            )}
          </div>
        </td>
        {currentColumns.map((columnId) => (
          <td key={columnId} className="p-3 border-b">
            <EditableCell
              value={product[columnId]}
              fieldId={columnId}
              product={product}
              rowIndex={index}
              isEditing={isEditing(index, columnId)}
              editingCell={editingCell}
              readOnly={readOnly}
              onCellEdit={onCellEdit}
              onCellValueChange={onCellValueChange}
              onCellKeyPress={onCellKeyPress}
              onCellSave={onCellSave}
              onProductIdClick={onProductIdClick}
              calculateColumnValue={calculateColumnValue}
              isCalculatedColumn={isCalculatedColumn}
              taxRate={taxRate}
              canViewPrices={canViewPrices}
            />
          </td>
        ))}
        {!readOnly && (
          <td className="p-3 border-b text-center">
            <div className="flex gap-1 justify-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handlePriceLookupClick}
                      className="h-8 w-8 text-primary"
                    >
                      <Calculator className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>价格计算</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {onReplace && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleReplaceClick}
                        className="h-8 w-8 text-primary"
                      >
                        <ArrowLeftRight className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>替换产品</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {hasAttachments && onAttachmentClick && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onAttachmentClick(product, index)}
                        className="h-8 w-8 text-secondary-foreground"
                      >
                        <Paperclip className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>查看附件</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleDeleteClick}
                      className="h-8 w-8 text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>删除</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </td>
        )}
      </tr>

      {enableExpandableProgress && isExpanded && executionProgress && (
        <tr>
          <td
            colSpan={currentColumns.length + (readOnly ? 1 : 2)}
            className="p-0 border-b"
          >
            <div className="bg-muted/50 p-4 m-2 rounded-lg border">
              <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
                {inventoryData && (
                  <div>
                    <div className="flex items-center gap-1 mb-2">
                      <Package className="h-4 w-4 text-success" />
                      <span className="text-sm font-semibold">库存状态</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">可用</span>
                        <span
                          className={cn(
                            'font-medium cursor-pointer hover:underline',
                            (inventoryData.available_quantity || 0) >= required
                              ? 'text-success'
                              : (inventoryData.available_quantity || 0) > 0
                              ? 'text-warning'
                              : 'text-destructive'
                          )}
                          onClick={() => onInventoryClick?.(product)}
                          title="点击查看库存详情"
                        >
                          {inventoryData.available_quantity || 0} 件
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">满足</span>
                        <span className="font-medium text-info">
                          {breakdown?.stockCoverage || 0} 件
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">预留</span>
                        <span className="text-muted-foreground">
                          {inventoryData.reserved_quantity || 0}
                        </span>
                      </div>
                      <div className="mt-1">
                        <Progress
                          value={
                            required > 0
                              ? Math.min(
                                  100,
                                  ((breakdown?.stockCoverage || 0) / required) * 100
                                )
                              : 0
                          }
                          className="h-1"
                        />
                        <span className="text-xs text-muted-foreground">
                          {required > 0
                            ? Math.round(((breakdown?.stockCoverage || 0) / required) * 100)
                            : 0}
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <ShoppingCart className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">采购分配</span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">已分配</span>
                      <span className="font-medium text-success">
                        {procurementData.allocated_qty || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">待创建</span>
                      <span className="text-muted-foreground">
                        {procurementData.pending_po_creation || 0}
                      </span>
                    </div>
                    <div className="mt-1">
                      <Progress
                        value={
                          (breakdown?.from_procurement || required) > 0
                            ? ((procurementData.allocated_qty || 0) /
                                (breakdown?.from_procurement || required)) *
                              100
                            : 0
                        }
                        className="h-1"
                      />
                      <span className="text-xs text-muted-foreground">
                        {(breakdown?.from_procurement || required) > 0
                          ? Math.round(
                              ((procurementData.allocated_qty || 0) /
                                (breakdown?.from_procurement || required)) *
                                100
                            )
                          : 0}
                        %
                      </span>
                    </div>
                  </div>
                </div>

                {productionData && productionData.allocated_qty > 0 && (
                  <div>
                    <div className="flex items-center gap-1 mb-2">
                      <Factory className="h-4 w-4 text-purple-500" />
                      <span className="text-sm font-semibold">生产</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">计划</span>
                        <span className="font-medium">{productionData.allocated_qty}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">完成</span>
                        <span className="font-medium text-purple-600">
                          {productionData.completed_qty}
                        </span>
                      </div>
                      <div className="mt-1">
                        <Progress
                          value={
                            productionData.allocated_qty > 0
                              ? (productionData.completed_qty / productionData.allocated_qty) *
                                100
                              : 0
                          }
                          className="h-1 bg-purple-200 [&>div]:bg-purple-500"
                        />
                        <span className="text-xs text-purple-600">
                          {productionData.allocated_qty > 0
                            ? Math.round(
                                (productionData.completed_qty / productionData.allocated_qty) *
                                  100
                              )
                            : 0}
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <Package className="h-4 w-4 text-info" />
                    <span className="text-sm font-semibold">入库接收</span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">已入库</span>
                      <span className="font-medium text-success">
                        {procurementData.received_qty || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">待收货</span>
                      <span className="text-muted-foreground">
                        {procurementData.pending_qty || 0}
                      </span>
                    </div>
                    <div className="mt-1">
                      <Progress
                        value={
                          (breakdown?.from_procurement || required) > 0
                            ? ((procurementData.received_qty || 0) /
                                (breakdown?.from_procurement || required)) *
                              100
                            : 0
                        }
                        className="h-1"
                      />
                      <span className="text-xs text-muted-foreground">
                        {(breakdown?.from_procurement || required) > 0
                          ? Math.round(
                              ((procurementData.received_qty || 0) /
                                (breakdown?.from_procurement || required)) *
                                100
                            )
                          : 0}
                        %
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <Truck className="h-4 w-4 text-warning" />
                    <span className="text-sm font-semibold">出库发货</span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">已发货</span>
                      <span className="font-medium text-success">
                        {warehouseData.outbound.shipped_qty}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">待发货</span>
                      <span className="text-muted-foreground">
                        {warehouseData.outbound.pending_qty}
                      </span>
                    </div>
                    <div className="mt-1">
                      <Progress
                        value={required > 0 ? (warehouseData.outbound.shipped_qty / required) * 100 : 0}
                        className="h-1"
                      />
                      <span className="text-xs text-muted-foreground">
                        {required > 0
                          ? Math.round((warehouseData.outbound.shipped_qty / required) * 100)
                          : 0}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {breakdown && (
                <div className="mt-4 pt-3 border-t border-border">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">总体进度:</span>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex-1 h-2 bg-muted-foreground/20 rounded-full overflow-hidden flex cursor-pointer">
                            <div
                              className="h-full bg-success transition-all"
                              style={{ width: `${breakdown.stockFulfilledPct}%` }}
                            />
                            <div
                              className="h-full bg-info transition-all"
                              style={{ width: `${breakdown.procurementFulfilledPct}%` }}
                            />
                            {productionData?.allocated_qty > 0 && (
                              <div
                                className="h-full bg-purple-500 transition-all"
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (productionData.completed_qty / productionData.allocated_qty) *
                                      100
                                  )}%`,
                                }}
                              />
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-xs">
                            <p className="mb-1">履约进度明细:</p>
                            <p>
                              • 库存满足: {breakdown.stockCoverage} 件 (
                              {breakdown.stockFulfilledPct.toFixed(1)}%)
                            </p>
                            <p>
                              • 采购完成: {breakdown.procurementAllocated} 件 (
                              {breakdown.procurementFulfilledPct.toFixed(1)}%)
                            </p>
                            <p className="mt-1">总体完成度: {overallProgress}%</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <span
                      className={cn(
                        'text-xs font-medium min-w-[45px]',
                        overallProgress >= 100
                          ? 'text-success'
                          : overallProgress >= 50
                          ? 'text-primary'
                          : 'text-muted-foreground'
                      )}
                    >
                      {overallProgress}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default ProductTableRow;
